import React, { useState, useRef } from "react";
import { Button, Input, message, Modal, Space } from "antd";
import type { InputRef, TableColumnsType } from 'antd';
import type { ColumnType, FilterConfirmProps } from 'antd/es/table/interface';
import { SearchOutlined } from "@ant-design/icons";
import useSWR from "swr";
import axios from "axios";

export interface ApprovalData {
    id?: number;
    code?: string;
    key: string;
    date_request: string;
    no_document: string;
    request_by: string;
    request_by_nama?: string;
    amount: number;
    remark: string;
    warehouse: string;
    wo_close_order?: string | number;
    service_qcp?: string | number;
    purchase_order?: string;
    close_purchase_order?: string | number;
    edit_purchase_order?: string | number;
    sales_close_order?: string | number;
    revise_work_order?: string | number;
    close_invoice_sales_order?: string | number;
    close_invoice_work_order?: string | number;
    return_sales_order?: string | number;
    return_work_order?: string | number;
    return_gr?: string | number;
    inventory?: string | number;
    spareparts_detail?: string | number;
}

export const useApprovalTable = (activeTab?: string) => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const searchInput = useRef<InputRef>(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const fetcher = async (url: string) => {
        const res = await fetch(`${backendUrl}${url}`);
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
    };

    const { data: approvals = [], error, isLoading, mutate } = useSWR<ApprovalData[]>('/api/approvals', fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: keyof ApprovalData,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: keyof ApprovalData): ColumnType<ApprovalData> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            (record[dataIndex]?.toString() ?? '')
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
    });

    const columns: TableColumnsType<ApprovalData> = [
        {
            title: 'Date Request',
            dataIndex: 'date_request',
            key: 'date_request',
            sorter: (a, b) => (a.date_request || '').localeCompare(b.date_request || ''),
            ...getColumnSearchProps('date_request'),
            render: (text: string) => {
                if (!text) return '';
                const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})/);
                if (match) {
                    const [, year, month, day, hours, minutes, seconds] = match;
                    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
                }
                return text;
            }
        },
        {
            title: 'No Document',
            dataIndex: 'no_document',
            key: 'no_document',
            sorter: (a, b) => (a.no_document || '').localeCompare(b.no_document || ''),
            ...getColumnSearchProps('no_document'),
        },
        {
            title: 'Request By',
            dataIndex: 'request_by',
            key: 'request_by',
            sorter: (a, b) => (a.request_by_nama || a.request_by || '').localeCompare(b.request_by_nama || b.request_by || ''),
            ...getColumnSearchProps('request_by'),
            render: (text, record) => record.request_by_nama || text,
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
            ...getColumnSearchProps('amount'),
            render: (value: number) => {
                if (value === undefined || value === null) return '';
                const parts = value.toString().split('.');
                const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                if (parts.length > 1) {
                    const decimalPart = parts[1].substring(0, 2);
                    return decimalPart ? `${integerPart},${decimalPart}` : integerPart;
                }
                return integerPart;
            }
        },
        {
            title: 'Remark',
            dataIndex: 'remark',
            key: 'remark',
            sorter: (a, b) => (a.remark || '').localeCompare(b.remark || ''),
            ...getColumnSearchProps('remark'),
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            sorter: (a, b) => (a.warehouse || '').localeCompare(b.warehouse || ''),
            ...getColumnSearchProps('warehouse'),
        },
    ];

    const data = approvals.map((item) => ({
        ...item,
        key: item.id?.toString(),
    }));

    React.useEffect(() => {
        if (selectedRowKeys.length > 1) {
            const signatures = new Set<string>();
            let hasMismatch = false;
            for (const key of selectedRowKeys) {
                const record = data.find(item => item.key === key);
                if (record) {
                    const signature = JSON.stringify({
                        code: record.code,
                        isCloseOrder: record.wo_close_order === '1' || record.wo_close_order === 1,
                        isQcPass: record.service_qcp === '1' || record.service_qcp === 1,
                        isClosePO: record.code === '06' && (record.close_purchase_order === '1' || record.close_purchase_order === 1),
                        isEditPO: record.code === '06' && (record.edit_purchase_order === '1' || record.edit_purchase_order === 1),
                        isCloseInvoiceSO: record.close_invoice_sales_order === '1' || record.close_invoice_sales_order === 1,
                        isCloseInvoiceWO: record.close_invoice_work_order === '1' || record.close_invoice_work_order === 1,
                        isSalesCloseOrder: record.sales_close_order === '1' || record.sales_close_order === 1,
                        isReviseWorkOrder: record.revise_work_order === '1' || record.revise_work_order === 1,
                    });
                    signatures.add(signature);
                    if (signatures.size > 1) {
                        hasMismatch = true;
                        break;
                    }
                }
            }
            if (hasMismatch) {
                message.warning("Pilih item dengan tipe logika pemrosesan yang sama!");
                setSelectedRowKeys([]);
            }
        }
    }, [selectedRowKeys, data]);

    const isRejectDisabled = selectedRowKeys.some(key => {
        const record = data.find(item => item.key === key);
        if (record?.sales_close_order === '1' || record?.sales_close_order === 1) return true;
        if (record?.code === '06') {
            if (record?.close_purchase_order === '1' || record?.close_purchase_order === 1 || record?.edit_purchase_order === '1' || record?.edit_purchase_order === 1) return false;
            return true;
        }
        if (record?.code === '03') return false;
        if (record?.code === '07') return false;
        if (record?.revise_work_order === '1' || record?.revise_work_order === 1) return false;
        return record?.wo_close_order === '1' || record?.wo_close_order === 1;
    });

    const handleAccept = async () => {
        if (selectedRowKeys.length === 0) {
            Modal.warning({ title: 'Peringatan', content: 'Pilih dokumen yang akan disetujui!' });
            return;
        }

        if (activeTab === 'spareparts_detail') {
            Modal.confirm({
                title: 'Konfirmasi Persetujuan',
                content: 'Apakah Anda yakin ingin menyetujui Spareparts Detail yang dipilih?',
                okText: 'Ya',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record) continue;
                            const response = await fetch(`${backendUrl}/api/approvals/accept`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({ no_document: record.no_document, approval_remark: '' })
                            });
                            if (!response.ok) throw new Error('Gagal menyetujui dokumen ' + record.no_document);
                        }
                        Modal.success({ title: 'Sukses', content: 'Spareparts Detail berhasil disetujui.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }

        const isReturnGR = selectedRowKeys.some(key => {
            const record = data.find(item => item.key === key);
            return record?.code === '07';
        });

        const allCloseOrder = selectedRowKeys.every(key => {
            const record = data.find(item => item.key === key);
            return record?.wo_close_order === '1' || record?.wo_close_order === 1;
        });

        if (allCloseOrder) {
            Modal.confirm({
                title: 'Konfirmasi Close Order',
                content: 'Apakah Anda yakin ingin menutup (close) dokumen yang dipilih?',
                okText: 'Ya',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record) continue;
                            const response = await fetch(`${backendUrl}/api/approvals/close-order`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({
                                    no_document: record.no_document
                                })
                            });
                            if (!response.ok) {
                                throw new Error('Gagal menutup dokumen ' + record.no_document);
                            }
                        }
                        Modal.success({ title: 'Sukses', content: 'Dokumen berhasil ditutup (closed).' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }

        const allQcPass = selectedRowKeys.every(key => {
            const record = data.find(item => item.key === key);
            return record?.service_qcp === '1' || record?.service_qcp === 1;
        });

        if (allQcPass) {
            Modal.confirm({
                title: 'Konfirmasi Approve QC Pass',
                content: 'Apakah Anda yakin ingin menyetujui QC Pass untuk dokumen yang dipilih?',
                okText: 'Ya',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record) continue;
                            const response = await fetch(`${backendUrl}/api/approvals/accept-qcpass`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({
                                    no_document: record.no_document
                                })
                            });
                            if (!response.ok) {
                                throw new Error('Gagal approve QC Pass dokumen ' + record.no_document);
                            }
                        }
                        Modal.success({ title: 'Sukses', content: 'QC Pass berhasil disetujui.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }

        const isMultiSelect = selectedRowKeys.length > 1;
        if (isMultiSelect) {
            Modal.confirm({
                title: 'Konfirmasi Persetujuan',
                content: 'Apakah Anda yakin ingin menyetujui semua dokumen yang dipilih?',
                okText: 'Ya',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record) continue;
                            if (record.code === '01' && (record.sales_close_order === '1' || record.sales_close_order === 1)) {
                                try {
                                    const logResponse = await fetch(`${backendUrl}/api/log-spareparts/sales_close_order`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                        body: JSON.stringify({ so_no: record.no_document })
                                    });
                                    if (logResponse.ok) {
                                        console.log(`[LOG_SPAREPARTS] Berhasil terkirim untuk dokumen: ${record.no_document}`);
                                    } else {
                                        console.error(`[LOG_SPAREPARTS] Gagal terkirim untuk dokumen: ${record.no_document}. Status: ${logResponse.status}`);
                                    }
                                } catch (logErr) {
                                    console.error(`[LOG_SPAREPARTS] Error koneksi saat kirim log ${record.no_document}:`, logErr);
                                }
                            }
                            const response = await fetch(`${backendUrl}/api/approvals/accept`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({
                                    no_document: record.no_document,
                                    approval_remark: ''
                                })
                            });
                            if (!response.ok) {
                                throw new Error('Gagal menyetujui dokumen ' + record.no_document);
                            }
                            if (record.code === '04' && String(record.return_sales_order) === '1') {
                                await fetch(`${backendUrl}/api/log-spareparts/sales_order_return`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                    body: JSON.stringify({ invoice_return_no: record.no_document })
                                });
                            }
                            if (record.code === '03' && String(record.return_work_order) === '1') {
                                await fetch(`${backendUrl}/api/log-spareparts/work_order_return`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                    body: JSON.stringify({ invoice_return_no: record.no_document })
                                });
                            }
                            if (record.code === '07' || String(record.return_gr) === '1') {
                                await fetch(`${backendUrl}/api/log-spareparts/return_gr`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                    body: JSON.stringify({ goods_receive_return_no: record.no_document })
                                });
                            }
                        }
                        Modal.success({ title: 'Sukses', content: 'Dokumen berhasil disetujui.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }
        let approvalRemark = '';
        Modal.confirm({
            title: 'Konfirmasi Persetujuan',
            content: (
                <div>
                    <p>Apakah Anda yakin ingin menyetujui dokumen yang dipilih?</p>
                </div>
            ),
            okText: 'Ya',
            cancelText: 'Tidak',
            onOk: async () => {
                setIsProcessing(true);
                try {
                    for (const key of selectedRowKeys) {
                        const record = data.find(item => item.key === key);
                        if (!record) continue;
                        if (record.code === '01' && (record.sales_close_order === '1' || record.sales_close_order === 1)) {
                            try {
                                const logResponse = await fetch(`${backendUrl}/api/log-spareparts/sales_close_order`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                    body: JSON.stringify({ so_no: record.no_document })
                                });
                                if (logResponse.ok) {
                                    console.log(`[LOG_SPAREPARTS] Berhasil terkirim untuk dokumen: ${record.no_document}`);
                                } else {
                                    console.error(`[LOG_SPAREPARTS] Gagal terkirim untuk dokumen: ${record.no_document}. Status: ${logResponse.status}`);
                                }
                            } catch (logErr) {
                                console.error(`[LOG_SPAREPARTS] Error koneksi saat kirim log ${record.no_document}:`, logErr);
                            }
                        }
                        const response = await fetch(`${backendUrl}/api/approvals/accept`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                            },
                            body: JSON.stringify({
                                no_document: record.no_document,
                                approval_remark: approvalRemark
                            })
                        });
                        if (!response.ok) {
                            throw new Error('Gagal menyetujui dokumen ' + record.no_document);
                        }
                        if (record.code === '04' && String(record.return_sales_order) === '1') {
                            await fetch(`${backendUrl}/api/log-spareparts/sales_order_return`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                body: JSON.stringify({ invoice_return_no: record.no_document })
                            });
                        }
                        if (record.code === '03' && String(record.return_work_order) === '1') {
                            await fetch(`${backendUrl}/api/log-spareparts/work_order_return`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                body: JSON.stringify({ invoice_return_no: record.no_document })
                            });
                        }
                        if (record.code === '07' || String(record.return_gr) === '1') {
                            await fetch(`${backendUrl}/api/log-spareparts/return_gr`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                body: JSON.stringify({ goods_receive_return_no: record.no_document })
                            });
                        }
                    }
                    Modal.success({ title: 'Sukses', content: 'Dokumen berhasil disetujui.' });
                    setSelectedRowKeys([]);
                    mutate();
                } catch (error: any) {
                    Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                } finally { setIsProcessing(false); }
            }
        });
    };

    const handleRevise = async () => {
        if (selectedRowKeys.length === 0) {
            Modal.warning({ title: 'Peringatan', content: 'Pilih dokumen yang akan direvisi!' });
            return;
        }

        const hasCloseInvoiceSO = selectedRowKeys.some(key => {
            const record = data.find(item => item.key === key);
            return record && (record.close_invoice_sales_order === '1' || record.close_invoice_sales_order === 1);
        });

        if (hasCloseInvoiceSO) {
            Modal.info({ title: 'Informasi', content: 'Proses Unknown' });
            return;
        }

        const hasCloseInvoiceWO = selectedRowKeys.some(key => {
            const record = data.find(item => item.key === key);
            return record && (record.close_invoice_work_order === '1' || record.close_invoice_work_order === 1);
        });

        if (hasCloseInvoiceWO) {
            Modal.info({ title: 'Proses Unknown' });
            return;
        }

        const hasReturnWO = selectedRowKeys.some(key => {
            const record = data.find(item => item.key === key);
            return record && record.code === '03' && (record.return_work_order === '1' || record.return_work_order === 1);
        });

        if (hasReturnWO) {
            Modal.info({ title: 'Informasi', content: 'Process Unknown' });
            return;
        }

        if (activeTab === 'return_sales_order') {
            setIsProcessing(true);
            try {
                for (const key of selectedRowKeys) {
                    const record = data.find(item => item.key === key);
                    if (record) {
                        const response = await fetch(`${backendUrl}/api/approvals/revise`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                            },
                            body: JSON.stringify({ no_document: record.no_document, approval_remark: '' })
                        });
                        if (!response.ok) throw new Error("Gagal mengubah status");
                    }
                }
                message.success('Status diubah ke Draft (1)');
                mutate();
                setSelectedRowKeys([]);
            } catch (error: any) { message.error(error.message || 'Gagal merevisi'); } finally { setIsProcessing(false); }
            return;
        }

        const isReturnWorkOrder = selectedRowKeys.some(key => {
            const record = data.find(item => item.key === key);
            return record?.code === '03';
        });

        if (isReturnWorkOrder) {
            Modal.confirm({
                title: 'Konfirmasi Revisi Return',
                content: 'Apakah Anda yakin ingin mengembalikan status Return Work Order yang dipilih ke Draft?',
                okText: 'Ya, Revisi',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record || record.code !== '03') continue;
                            const reviseApproveRes = await fetch(`${backendUrl}/api/approvals/revise`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({ no_document: record.no_document, approval_remark: '' })
                            });

                            if (!reviseApproveRes.ok) throw new Error("Gagal mengupdate status approval.");
                        }
                        Modal.success({ title: 'Sukses', content: 'Dokumen return berhasil dikembalikan ke Draft.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }

        const isReturnSO = selectedRowKeys.some(key => {
            const record = data.find(item => item.key === key);
            return record?.code === '04';
        });

        if (isReturnSO) {
            Modal.confirm({
                title: 'Konfirmasi Approval Return SO',
                content: 'Apakah Anda yakin ingin menyetujui Return Sales Order ini?',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (record?.code === '04') {
                                await fetch(`${backendUrl}/api/approvals/accept`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                                    body: JSON.stringify({ no_document: record.no_document, approval_remark: '' })
                                });
                            }
                        }
                        message.success('Return SO berhasil disetujui');
                        setSelectedRowKeys([]); mutate();
                    } catch (e) { message.error('Gagal approve'); } finally { setIsProcessing(false); }
                }
            });
            return;
        }

        const isMultiSelect = selectedRowKeys.length > 1;
        if (isMultiSelect) {
            Modal.confirm({
                title: 'Konfirmasi Revisi',
                content: 'Apakah Anda yakin ingin merevisi semua dokumen yang dipilih? Status akan kembali ke Draft.',
                okText: 'Ya, Revisi',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record) continue;
                            const response = await fetch(`${backendUrl}/api/approvals/revise`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({
                                    no_document: record.no_document,
                                    approval_remark: ''
                                })
                            });
                            if (!response.ok) {
                                throw new Error('Gagal merevisi dokumen ' + record.no_document);
                            }
                        }
                        Modal.success({ title: 'Sukses', content: 'Dokumen berhasil direvisi.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }
        let revisiRemark = '';
        Modal.confirm({
            title: 'Konfirmasi Revisi',
            content: (
                <div>
                    <p>Apakah Anda yakin ingin merevisi dokumen yang dipilih? Status akan kembali ke Draft.</p>
                </div>
            ),
            okText: 'Ya, Revisi',
            cancelText: 'Tidak',
            onOk: async () => {
                setIsProcessing(true);
                try {
                    for (const key of selectedRowKeys) {
                        const record = data.find(item => item.key === key);
                        if (!record) continue;
                        const response = await fetch(`${backendUrl}/api/approvals/revise`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                            },
                            body: JSON.stringify({
                                no_document: record.no_document,
                                approval_remark: revisiRemark
                            })
                        });
                        if (!response.ok) {
                            throw new Error('Gagal merevisi dokumen ' + record.no_document);
                        }
                    }
                    Modal.success({ title: 'Sukses', content: 'Dokumen berhasil direvisi.' });
                    setSelectedRowKeys([]);
                    mutate();
                } catch (error: any) {
                    Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                } finally { setIsProcessing(false); }
            }
        });
    };

    const handleReject = async () => {
        if (selectedRowKeys.length === 0) {
            Modal.warning({ title: 'Peringatan', content: 'Pilih dokumen yang akan ditolak!' });
            return;
        }

        if (activeTab === 'return_sales_order') {
            setIsProcessing(true);
            try {
                for (const key of selectedRowKeys) {
                    const record = data.find(item => item.key === key);
                    if (record) {
                        const response = await fetch(`${backendUrl}/api/approvals/reject`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                            },
                            body: JSON.stringify({ no_document: record.no_document, approval_remark: '' })
                        });
                        if (!response.ok) throw new Error("Gagal menolak Return SO");
                    }
                }
                message.success('Status diubah ke Rejected (5)');
                mutate();
                setSelectedRowKeys([]);
            } catch (error: any) { message.error(error.message || 'Gagal memproses penolakan'); } finally { setIsProcessing(false); }
            return;
        }

        if (activeTab === 'spareparts_detail') {
            let rejectRemark = '';
            Modal.confirm({
                title: 'Konfirmasi Reject',
                content: (
                    <div>
                        <p>Apakah Anda yakin ingin menolak Spareparts Detail?</p>
                    </div>
                ),
                okText: 'Ya, Reject',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record) continue;
                            const response = await fetch(`${backendUrl}/api/approvals/reject`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({ no_document: record.no_document, approval_remark: rejectRemark })
                            });
                            if (!response.ok) throw new Error('Gagal menolak dokumen ' + record.no_document);
                        }
                        Modal.success({ title: 'Sukses', content: 'Spareparts Detail berhasil ditolak.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }

        const isReturnWorkOrder = selectedRowKeys.some(key => {
            const record = data.find(item => item.key === key);
            return record?.code === '03';
        });

        if (isReturnWorkOrder) {
            Modal.confirm({
                title: 'Konfirmasi Reject Return',
                content: 'Apakah Anda yakin ingin membatalkan Return Work Order yang dipilih?',
                okText: 'Ya, Reject',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record || record.code !== '03') continue;

                            const rejectApproveRes = await fetch(`${backendUrl}/api/approvals/reject`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({ no_document: record.no_document, approval_remark: '' })
                            });

                            if (!rejectApproveRes.ok) throw new Error("Gagal mengupdate status approval.");
                        }
                        Modal.success({ title: 'Sukses', content: 'Dokumen return berhasil dibatalkan.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }

        const allQcPassReject = selectedRowKeys.every(key => {
            const record = data.find(item => item.key === key);
            return record?.service_qcp === '1' || record?.service_qcp === 1;
        });

        if (allQcPassReject) {
            Modal.confirm({
                title: 'Konfirmasi Reject QC Pass',
                content: 'Apakah Anda yakin ingin menolak QC Pass? Status akan kembali ke "On Going".',
                okText: 'Ya, Reject',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record) continue;
                            const response = await fetch(`${backendUrl}/api/approvals/reject-qcpass`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({
                                    no_document: record.no_document
                                })
                            });
                            if (!response.ok) {
                                throw new Error('Gagal reject QC Pass dokumen ' + record.no_document);
                            }
                        }
                        Modal.success({ title: 'Sukses', content: 'QC Pass berhasil ditolak. Status kembali ke On Going.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }

        const isMultiSelect = selectedRowKeys.length > 1;
        if (isMultiSelect) {
            Modal.confirm({
                title: 'Konfirmasi Penolakan',
                content: 'Apakah Anda yakin ingin menolak semua dokumen yang dipilih?',
                okText: 'Ya, Reject',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProcessing(true);
                    try {
                        for (const key of selectedRowKeys) {
                            const record = data.find(item => item.key === key);
                            if (!record) continue;
                            const response = await fetch(`${backendUrl}/api/approvals/reject`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                },
                                body: JSON.stringify({
                                    no_document: record.no_document,
                                    approval_remark: ''
                                })
                            });
                            if (!response.ok) {
                                throw new Error('Gagal menolak dokumen ' + record.no_document);
                            }
                        }
                        Modal.success({ title: 'Sukses', content: 'Dokumen berhasil ditolak.' });
                        setSelectedRowKeys([]);
                        mutate();
                    } catch (error: any) {
                        Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                    } finally { setIsProcessing(false); }
                }
            });
            return;
        }
        let rejectRemark = '';
        Modal.confirm({
            title: 'Konfirmasi Penolakan',
            content: (
                <div>
                    <p>Apakah Anda yakin ingin menolak dokumen yang dipilih?</p>
                </div>
            ),
            okText: 'Ya, Reject',
            cancelText: 'Tidak',
            onOk: async () => {
                setIsProcessing(true);
                try {
                    for (const key of selectedRowKeys) {
                        const record = data.find(item => item.key === key);
                        if (!record) continue;
                        const response = await fetch(`${backendUrl}/api/approvals/reject`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                            },
                            body: JSON.stringify({
                                no_document: record.no_document,
                                approval_remark: rejectRemark
                            })
                        });
                        if (!response.ok) {
                            throw new Error('Gagal menolak dokumen ' + record.no_document);
                        }
                    }
                    Modal.success({ title: 'Sukses', content: 'Dokumen berhasil ditolak.' });
                    setSelectedRowKeys([]);
                    mutate();
                } catch (error: any) {
                    Modal.error({ title: 'Gagal', content: error.message || 'Terjadi kesalahan' });
                } finally { setIsProcessing(false); }
            }
        });
    };

    const isReviseDisabled = activeTab === 'spareparts_detail' || selectedRowKeys.some(key => {
        const record = data.find(item => item.key === key);
        if (record?.code === '07') return false;
        if (record?.code === '06' && (record?.close_purchase_order === '1' || record?.close_purchase_order === 1 || record?.edit_purchase_order === '1' || record?.edit_purchase_order === 1)) return true;
        if (record?.revise_work_order === '1' || record?.revise_work_order === 1) return true;
        return record?.service_qcp === '1' || record?.service_qcp === 1;
    });

    return { columns, data, isLoading, error, selectedRowKeys, setSelectedRowKeys, handleAccept, handleRevise, handleReject, isRejectDisabled, isReviseDisabled, isProcessing };
};